import React from 'react'
import T from 'prop-types'
import VisibilitySensor from 'react-visibility-sensor'
import { styled } from '@mui/material/styles'
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import theme from 'theme'
import { getUrlWithCorrectProtocol } from 'lib/getCarPictures'

import CarSpecifications from '../organisms/SessionsConfirmation/CarSpecifications'

const classes = {
  itemGrid: 'item-grid',
  carImage: 'car-image',
  carContent: 'car-content',
  carTitle: 'car-title',
  carSubtitle: 'car-subtitle',
  carPrice: 'car-price',
}
const Root = styled(Paper)(({ theme, height }) => ({
  height: `${height}px`, // The fixed height is required to stabilize the VisibilitySensor
  marginBottom: theme.spacing(1.5),
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  [`& .${classes.itemGrid}`]: {
    display: 'grid',
    gridTemplateColumns: '3fr 9fr 1.5fr',
    columnGap: theme.spacing(2),
    [`& .${classes.carImage}`]: {
      display: 'block',
      maxWidth: '100%',
      maxHeight: theme.custom.carCardMaxHeight,
      borderRadius: theme.shape.borderRadius,
    },
  },
  [`& .${classes.carContent}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    [`& .${classes.carTitle}`]: {
      paddingBottom: theme.spacing(0.5),
    },
    [`& .${classes.carSubtitle}, & .${classes.carPrice}`]: {
      paddingBottom: theme.spacing(1),
    },
  },
}))

function CarCard({
  onClick,
  car,
  price,
  maxPrice,
  showPriceRange = true,
  right,
  height = theme.custom.carCardMaxHeight,
  rightProps,
  bottom,
  bottomProps,
  onToggleFavorite,
  showFavoriteReadOnly = false,
  ...others
}) {
  const numberFormatter = new Intl.NumberFormat('it-IT')

  const handleFavoriteClick = (e) => {
    e.stopPropagation()
    if (onToggleFavorite) {
      onToggleFavorite()
    }
  }

  const showFavoriteIcon = onToggleFavorite || showFavoriteReadOnly

  return (
    <VisibilitySensor partialVisibility>
      {({ isVisible }) => {
        return isVisible ? (
          <Root height={height} {...others}>
            <Box className={classes.itemGrid}>
              <Box onClick={onClick} sx={{ cursor: 'pointer' }}>
                <Box
                  component="img"
                  src={getUrlWithCorrectProtocol(car.pictureurl)}
                  className={classes.carImage}
                  alt={car.title}
                  sx={{ maxHeight: height, width: 'auto' }}
                />
              </Box>
              <Box
                onClick={onClick}
                className={classes.carContent}
                sx={{ cursor: 'pointer' }}
                data-testid="car-detail-click"
              >
                <div>
                  <Stack
                    direction="row"
                    alignItems="center"
                    className={classes.carTitle}
                  >
                    <Typography variant="body1" color="textPrimary">
                      <strong>
                        {car.brand} {car.model}
                      </strong>
                    </Typography>
                    {showFavoriteIcon && (
                      <IconButton
                        size="small"
                        onClick={handleFavoriteClick}
                        aria-label={
                          car.favorite
                            ? 'Remove from favorites'
                            : 'Add to favorites'
                        }
                        disabled={!onToggleFavorite}
                      >
                        {car.favorite ? (
                          <FavoriteIcon sx={{ fontSize: 20, color: 'red' }} />
                        ) : (
                          <FavoriteBorderIcon sx={{ fontSize: 20 }} />
                        )}
                      </IconButton>
                    )}
                  </Stack>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className={classes.carSubtitle}
                  >
                    {car.title}
                  </Typography>
                </div>
                <Typography
                  variant="body2"
                  color="primary"
                  className={classes.carPrice}
                >
                  <strong>
                    €{' '}
                    {(price !== undefined && price) || car.marketPrice
                      ? numberFormatter.format(
                          (price !== undefined && price) || car.marketPrice
                        )
                      : '-'}
                    {showPriceRange &&
                      maxPrice &&
                      ` - € ${numberFormatter.format(maxPrice)}`}
                  </strong>
                </Typography>
                <CarSpecifications car={car} />
              </Box>
              <Stack
                justifyContent="space-between"
                alignItems="flex-end"
                {...rightProps}
              >
                {right}
              </Stack>
            </Box>
            <Box {...bottomProps}>{bottom}</Box>
          </Root>
        ) : (
          <Root height={height} {...others} />
        )
      }}
    </VisibilitySensor>
  )
}

CarCard.propTypes = {
  linkTo: T.string,
  car: T.object.isRequired,
  price: T.number,
  maxPrice: T.number,
  showPriceRange: T.bool,
  right: T.node,
  rightProps: T.object,
  bottom: T.node,
  bottomProps: T.object,
  onToggleFavorite: T.func,
  showFavoriteReadOnly: T.bool,
}

export default CarCard
